import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { FileText, Calendar, CheckCircle, Clock, Eye, Edit } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const MyToolboxTalks = () => {
  const { user } = useContext(UserContext);
  const [talks, setTalks] = useState([]);
  const [filteredTalks, setFilteredTalks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'to-sign', 'signed'

  useEffect(() => {
    if (user && user.id) {
      fetchMyTalks();
    }
  }, [user]);

  useEffect(() => {
    filterTalks();
  }, [talks, activeTab]);

  const fetchMyTalks = async () => {
    if (!user?.accessToken) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/toolbox-talks/my-talks/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });

      console.log('My toolbox talks:', response.data);
      setTalks(response.data);
    } catch (error) {
      console.error('Error fetching my toolbox talks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTalks = () => {
    if (activeTab === 'all') {
      setFilteredTalks(talks);
    } else if (activeTab === 'to-sign') {
      setFilteredTalks(talks.filter(t => !t.has_signed));
    } else if (activeTab === 'signed') {
      setFilteredTalks(talks.filter(t => t.has_signed));
    }
  };

  const handleSignTalk = async (talkId) => {
    try {
      const signature = user.user_signature || '';
      await axios.post(
        `${API_BASE_URL}/toolbox-talks/${talkId}/sign/`,
        { signature },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );

      alert('Toolbox Talk signed successfully!');
      fetchMyTalks(); // Refresh list
    } catch (error) {
      console.error('Error signing toolbox talk:', error);
      alert(error.response?.data?.error || 'Failed to sign toolbox talk');
    }
  };

  const handlePreviewPDF = async (talk) => {
    try {
      const url = `${API_BASE_URL}/toolbox-talks/${talk.id}/pdf/?preview=true`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
        responseType: 'blob'
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error previewing PDF:', error);
      alert('Failed to preview PDF');
    }
  };

  const toSignCount = talks.filter(t => !t.has_signed).length;
  const signedCount = talks.filter(t => t.has_signed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <FileText className="w-8 h-8 text-blue-600" />
          My Toolbox Talks
        </h1>
        <p className="text-gray-600 mt-2">View and manage your toolbox talk signatures</p>
      </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'all'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All
            <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
              {talks.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('to-sign')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'to-sign'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            To Sign
            <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-600 rounded-full">
              {toSignCount}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('signed')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'signed'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Signed
            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-600 rounded-full">
              {signedCount}
            </span>
          </button>
        </div>

        {/* Table */}
        {filteredTalks.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No toolbox talks found</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Topic
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Supervisor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signed At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTalks.map((talk) => (
                    <tr key={talk.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {talk.has_signed ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Signed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                            <Edit className="w-3.5 h-3.5" />
                            To Sign
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{talk.topic}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{new Date(talk.date).toLocaleDateString()}</div>
                        {talk.time && (
                          <div className="text-xs text-gray-500">{talk.time}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{talk.project}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{talk.conductor}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {talk.has_signed && talk.signed_at ? (
                          <div className="text-sm text-gray-900">
                            <div>{new Date(talk.signed_at).toLocaleDateString()}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(talk.signed_at).toLocaleTimeString()}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handlePreviewPDF(talk)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                            title="Preview PDF"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!talk.has_signed && (
                            <button
                              onClick={() => handleSignTalk(talk.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                              title="Sign Now"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>Sign</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
};

export default MyToolboxTalks;
